import { useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation, PermissionStatus } from '@capacitor/geolocation';

import { useLogger } from '@mopla/logger';

import { GET_POS_TIMEOUT } from './constants';
import { useGeoPositionStore } from './useGeoPositionStore';

export interface IUseGeolocationPermission {
	/** Request geolocation permission (shows native OS dialog) */
	requestPermission: () => Promise<PermissionStatus | null>;
	/** Check current permission status without requesting */
	checkPermission: () => Promise<PermissionStatus | null>;
}

/**
 * Hook for managing geolocation permissions
 * Automatically syncs permission status to the store
 */
export const useGeolocationPermission = (): IUseGeolocationPermission => {
	const setPermissionStatus = useGeoPositionStore(
		(state) => state.setPermissionStatus
	);
	const logger = useLogger('CurrentGeoPositionV2');

	const checkPermission =
		useCallback(async (): Promise<PermissionStatus | null> => {
			try {
				const result = await Geolocation.checkPermissions();
				setPermissionStatus(result);
				return result;
			} catch (err) {
				logger.warn('Failed to check geolocation permissions', { error: err });
				return null;
			}
		}, [logger, setPermissionStatus]);

	const requestPermission =
		useCallback(async (): Promise<PermissionStatus | null> => {
			const isNative = Capacitor.isNativePlatform();

			try {
				if (isNative) {
					/** Native: explicitly request permissions */
					const result = await Geolocation.requestPermissions({
						permissions: ['location', 'coarseLocation'],
					});
					setPermissionStatus(result);
					return result;
				}

				/**
				 * The requestPermissions is not implemented for web, so getCurrentPosition will ask for permissions,
				 * then we need to check the permission status ourselves.
				 * */
				await Geolocation.getCurrentPosition({
					enableHighAccuracy: true,
					timeout: GET_POS_TIMEOUT,
				});

				return checkPermission();
			} catch (error) {
				logger.warn('Failed to request geolocation permissions', { error });

				/** Request failed — fallback to just check the current permission status */
				return checkPermission();
			}
		}, [setPermissionStatus, checkPermission, logger]);

	return {
		requestPermission,
		checkPermission,
	};
};
